import {
	IconButton,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Typography,
	Button,
	TextField,
	InputAdornment,
	Tooltip,
	Box,
	Chip,
	Alert,
	CircularProgress,
	Paper,
	Grid,
	Stack,
	Card,
} from '@mui/material'
import { DataGrid, GridSearchIcon } from '@mui/x-data-grid'
import {
	CancelRounded,
	CheckCircleRounded,
	CloudDownload,
	MeetingRoomRounded,
	Refresh,
	FilterList,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { closeDialog, showDialog } from '../../../actions'
import {
	approveCashRequestAction,
	getFundBalanceAction,
	getCashListAction,
} from '../../../actions/event'
import { ACCOMODATION_CATEGORY, EVENT_BOOKING_STATUS } from '../../../constants'
import { HIDE_MAIN_LOADER, SHOW_MAIN_LOADER } from '../../../constants/actions'
import { getCashList } from '../../../services/event'
import { updateBooking } from '../../../services/viplava'
import {
	exportFromJSON,
	getAreas,
	isAreaLeader,
	isMobileScreen,
} from '../../../utils'
import '../style.scss'
import { getFinalAmount } from '../../Viplava/utils'
import { CustomDataGrid } from '../../../components/CustomTable/CustomDataGrid'

const CashScreen = ({ eventId }) => {
	const [filters, setFilters] = useState({
		areaId: ['ALL'],
		accomodation: ['ALL'],
		transportation: ['ALL'],
		status: ['ALL'],
		page: 0,
		pageSize: 10,
	})
	const [loading, setLoading] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	const [error, setError] = useState(null)
	const [refreshing, setRefreshing] = useState(false)

	const dispatch = useDispatch()
	const eventReducer = useSelector((state) => state.eventReducer)
	const { cashRequests } = eventReducer
	const AREAS =
		localStorage.getItem('AREAS') &&
		JSON.parse(localStorage.getItem('AREAS'))
	const [areaId, setAreaId] = useState(AREAS?.[0]._id)

	// Debounced search function
	const debouncedSearch = useCallback(
		debounce((searchTerm) => {
			getCashData(searchTerm)
		}, 500),
		[filters, areaId]
	)


	const approveRequest = (data) => {
		if (data._id) {
			dispatch(
				showDialog({
					title: 'Approve this cash request?',
					description:
						"Are you sure to approve this cash request? This action won't be reverted",
					form: [
						{
							name: 'amount',
							label: 'Amount Received',
							placeholder: 'Input the Amount you recieved',
							defaultValue: data.tokenAmount,
						},
					],
					actions: [
						{
							action: dispatch(closeDialog()),
							text: 'Cancel',
							color: 'secondary',
						},
						{
							action: (dialogValue) => {
								dispatch(
									approveCashRequestAction({
										id: data._id,
										cashRecieved: dialogValue.amount,
									})
								)
								dispatch(closeDialog())
							},
							text: 'Yes, Approve request',
						},
					],
				})
			)
		}
	}

	const disproveRequest = (data) => {
		if (data._id) {
			dispatch(
				showDialog({
					title: 'Cancel this cash request?',
					description:
						"Are you sure to Cancel this cash request? This action won't be reverted",
					actions: [
						{
							action: dispatch(closeDialog()),
							text: 'Cancel',
							color: 'secondary',
						},
						{
							action: () => {
								cancelBooking(data)
								dispatch(closeDialog())
							},
							text: 'Yes, Cancel request',
						},
					],
				})
			)
		}
	}

	const rowWidthConfig = {
		name: { default: { flex: 0.12 }, mobile: { width: 150, hide: false } },
		phone: { default: { flex: 0.1 }, mobile: { width: 120, hide: true } },
		facilitator: { default: { flex: 0.12 }, mobile: { width: 120, hide: true } },
		transportation: {
			default: { flex: 0.08 },
			mobile: { width: 100, hide: true },
		},
		accomodationCategory: { default: { flex: 0.1 }, mobile: { width: 100, hide: true } },
		connectedArea: { default: { flex: 0.12 }, mobile: { width: 120, hide: true } },
		tokenAmount: { default: { flex: 0.08 }, mobile: { width: 80, hide: true } },
		finalAmount: { default: { flex: 0.08 }, mobile: { width: 100, hide: false } },
		paidAmount: { default: { flex: 0.08 }, mobile: { width: 100, hide: false } },
		status: { default: { flex: 0.08 }, mobile: { width: 80, hide: false } },
		subsidy: { default: { flex: 0.08 }, mobile: { width: 80, hide: true } },
		status_detailed: { default: { flex: 0.12 }, mobile: { width: 120, hide: true } },
		user: { default: { width: 200, flex: 0.15 }, mobile: { width: 150, hide: false } },
	}

	const headCells = [
		{
			field: 'name',
			numeric: false,
			headerName: 'Name',
			sortable: false,
			hide: isMobileScreen() ? rowWidthConfig.name.mobile.hide : false,
		},
		{
			field: 'phone',
			numeric: false,
			headerName: 'Phone',
			sortable: false,
			hide: isMobileScreen() ? rowWidthConfig.phone.mobile.hide : false,
		},
		{
			field: 'facilitator',
			numeric: false,
			headerName: 'Facilitator',
			sortable: false,
			hide: isMobileScreen() ? rowWidthConfig.facilitator.mobile.hide : false,
		},
		{
			field: 'transportation',
			numeric: false,
			headerName: 'Transportation',
			sortable: false,
			hide: isMobileScreen() ? rowWidthConfig.transportation.mobile.hide : false,
		},
		{
			field: 'accomodationCategory',
			numeric: false,
			headerName: 'Accomodation',
			sortable: false,
			hide: isMobileScreen() ? rowWidthConfig.accomodationCategory.mobile.hide : false,
		},
		{
			field: 'finalAmount',
			numeric: false,
			headerName: 'Total Amount',
			sortable: true,
			hide: isMobileScreen() ? rowWidthConfig.finalAmount.mobile.hide : false,
			renderCell: (params) => {
				return (
					<Tooltip
						title={getFinalAmount(params.row).join(',')}
						placement="top"
						arrow
					>
						<Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
							₹{params.value?.toLocaleString() || '0'}
						</Typography>
					</Tooltip>
				)
			},
		},
		{
			field: 'paidAmount',
			numeric: false,
			headerName: 'Amount paid',
			sortable: true,
			hide: isMobileScreen() ? rowWidthConfig.paidAmount.mobile.hide : false,
			renderCell: (params) => (
				<Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
					₹{params.value?.toLocaleString() || '0'}
				</Typography>
			),
		},
		{
			field: 'subsidy',
			numeric: false,
			headerName: 'Discount',
			sortable: false,
			hide: isMobileScreen() ? rowWidthConfig.subsidy.mobile.hide : false,
			renderCell: (params) => (
				<Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
					₹{params.value?.toLocaleString() || '0'}
				</Typography>
			),
		},
		{
			field: 'status',
			numeric: false,
			headerName: 'Pay mode',
			sortable: true,
			hide: isMobileScreen() ? rowWidthConfig.status.mobile.hide : false,
			renderCell: (params) => (
				<Chip
					label={params.value}
					size="small"
					color={params.value === 'Cash' ? 'primary' : 'secondary'}
					variant="outlined"
				/>
			),
		},
		{
			field: 'status_detailed',
			numeric: false,
			headerName: 'Status',
			sortable: true,
			hide: isMobileScreen() ? rowWidthConfig.status_detailed.mobile.hide : false,
			renderCell: (params) => (
				<Chip
					label={params.value}
					size="small"
					color={
						params.value?.includes('PENDING') ? 'warning' :
							params.value?.includes('APPROVED') ? 'success' :
								params.value?.includes('CANCELLED') ? 'error' : 'default'
					}
					variant="filled"
				/>
			),
		},
		{
			field: 'connectedArea',
			numeric: false,
			headerName: 'Area',
			sortable: false,
			hide: isMobileScreen() ? rowWidthConfig.connectedArea.mobile.hide : false,
		},
		{
			field: 'user',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			width: isMobileScreen() ? 150 : 200,
			hide: isMobileScreen() ? rowWidthConfig.user.mobile.hide : false,
			renderCell: (params) => (
				<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
					{!isAreaLeader() && (
						<Tooltip title="Approve Request">
							<IconButton
								onClick={() => approveRequest(params.row)}
								aria-label="Approve Request"
								color="success"
								size="small"
								sx={{
									backgroundColor: 'success.light',
									'&:hover': { backgroundColor: 'success.main', color: 'white' }
								}}
							>
								<CheckCircleRounded fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
					{params.row.accomodationCategory === 'Dormitory' &&
						params.row.status === 'Cash' && (
							<Tooltip title="Upgrade to Room">
								<IconButton
									onClick={() => upgradeToRoom(params.row)}
									aria-label="Upgrade to Room"
									color="info"
									size="small"
									sx={{
										backgroundColor: 'info.light',
										'&:hover': { backgroundColor: 'info.main', color: 'white' }
									}}
								>
									<MeetingRoomRounded fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
					{params.row.status === 'Cash' && (
						<Tooltip title="Cancel Request">
							<IconButton
								onClick={() => disproveRequest(params.row)}
								aria-label="Cancel Request"
								color="error"
								size="small"
								sx={{
									backgroundColor: 'error.light',
									'&:hover': { backgroundColor: 'error.main', color: 'white' }
								}}
							>
								<CancelRounded fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			),
		},
	]

	// Handle search input changes
	const handleSearchChange = (event) => {
		const value = event.target.value
		setSearchValue(value)
		debouncedSearch(value)
	}

	// Handle refresh
	const handleRefresh = () => {
		setRefreshing(true)
		getCashData(searchValue)
	}

	useEffect(() => {
		getCashData(searchValue)
	}, [filters, areaId])

	const download = () => {
		const todayDate = format(new Date(), 'dd-MM-yyyy')
		const fileName = 'Cash Requests ' + todayDate
		const filterAreaId =
			filters.areaId[0] === 'ALL' ? undefined : filters.areaId.join(',')
		dispatch({ type: SHOW_MAIN_LOADER })
		getCashList({
			page: 1,
			per_page: 5000,
			eventId,
			areaId: filterAreaId,
			status:
				filters.status.join(',') === 'ALL' || !filters.status.join(',')
					? '9,11,12'
					: filters.status.join(','),
		})
			.then((res) => {
				exportFromJSON({
					data: res.data?.data?.map((s) => ({
						name: s.userId?.initiatedName || s.userId?.name,
						email: s.userId?.email,
						phone: s.userId?.phone,
						facilitator:
							s.userId?.facilitator?.initiatedName ||
							s.userId?.facilitator?.name,
						connectedArea: s.connectedAreaId?.name,
						transportationAmount: s.transportationAmount,
						transportation:
							s.transportationAmount > 0 ? 'Yes' : 'No',
						accomodationCategory:
							ACCOMODATION_CATEGORY[s.accomodationCategory]?.name,
						status: EVENT_BOOKING_STATUS[s.status]?.includes('CASH')
							? 'Cash'
							: 'Online',
						status_detailed: EVENT_BOOKING_STATUS[s.status],
						totalAmount: s.finalAmount,
						// tokenAmount: s.tokenAmount,
						amountPaid: s.paidAmount,
						discount: s.subsidy,
					})),
					fileName,
				})
				dispatch({ type: HIDE_MAIN_LOADER })
			})
			.catch((e) => {
				dispatch({ type: HIDE_MAIN_LOADER })
				console.log(e)
			})
	}

	const upgradeToRoom = async (rowData) => {
		const booking = cashRequests.data.find(
			(req) => req.bookingReferenceNo === rowData.bookingReferenceNo
		)
		await updateBooking({
			...booking,
			eventId,
			userId: booking.userId._id,
			accomodationCategory: ACCOMODATION_CATEGORY.ROOM.id,
			transportationAmount: booking.transportationAmount,
			paymentMode: 'CASH',
			tokenAmount: booking.tokenAmount + 500 || 500,
		})
		getCashData()
	}

	const cancelBooking = async (rowData) => {
		const booking = cashRequests.data.find(
			(req) => req.bookingReferenceNo === rowData.bookingReferenceNo
		)

		await updateBooking({
			...booking,
			eventId,
			userId: booking.userId._id,
			accomodationCategory: booking.accomodationCategory,
			transportationAmount: booking.transportationAmount,
			tokenAmount: booking.tokenAmount || 500,
		})
		getCashData()
	}

	const getCashData = useCallback(
		(q) => {
			setLoading(true)
			setError(null)

			const filterAreaId =
				filters.areaId[0] === 'ALL'
					? undefined
					: filters.areaId.join(',')
			const accomodation =
				filters.accomodation[0] === 'ALL'
					? undefined
					: filters.accomodation.join(',')
			const transportation =
				filters.transportation[0] === 'ALL'
					? undefined
					: filters.transportation.join(',')
			const status =
				filters.status[0] === 'ALL' || filters.status.length === 0
					? '9,11,12'
					: filters.status.join(',')

			const actionPromise = isAreaLeader()
				? dispatch(
					getCashListAction({
						page: filters.page + 1,
						per_page: filters.pageSize,
						areaId,
						eventId,
						status,
						accomodation,
						transportation,
						q,
					})
				)
				: dispatch(
					getCashListAction({
						page: filters.page + 1,
						per_page: filters.pageSize,
						eventId,
						areaId: filterAreaId,
						status,
						accomodation,
						transportation,
						q,
					})
				)

			actionPromise
				.then(() => {
					setLoading(false)
					setRefreshing(false)
				})
				.catch((err) => {
					setError('Failed to load cash requests. Please try again.')
					setLoading(false)
					setRefreshing(false)
					console.error('Error loading cash data:', err)
				})

			if (isAreaLeader()) {
				dispatch(getFundBalanceAction(areaId))
			}
		},
		[filters, areaId]
	)

	const onPageSizeChange = ({ page, pageSize }) => {
		setFilters((f) => ({
			...f,
			page,
			pageSize,
		}))
	}

	const onFilterChange = (name, value) => {
		let whatAdded, whatRemoved
		if (value.length > filters[name].length) {
			whatAdded = value.find((f) => !filters[name].includes(f))
		} else {
			whatRemoved = filters[name].find((f) => !value.includes(f))
		}
		if (typeof whatAdded !== undefined) {
			if (whatAdded === 'ALL') value = ['ALL']
			else value = value.filter((e) => e !== 'ALL')
		} else if (typeof whatRemoved !== undefined) {
			if (whatRemoved === 'ALL') value = value.length ? value : ['ALL']
		}
		setFilters((f) => ({
			...f,
			[name]: value,
			page: 1,
		}))
	}

	return (
		<Box className="screen subsidy-screen cash-screen" sx={{ p: 3 }}>
			{/* Header Section */}
			<Box sx={{ mb: 3 }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
					<Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
						Cash Requests
					</Typography>
					<Stack direction="row" spacing={1}>
						<Button
							startIcon={<Refresh />}
							variant="outlined"
							onClick={handleRefresh}
							disabled={refreshing}
							size="small"
						>
							{refreshing ? <CircularProgress size={16} /> : 'Refresh'}
						</Button>
						<Button
							startIcon={<CloudDownload />}
							color="primary"
							variant="contained"
							onClick={download}
							size="small"
						>
							Download
						</Button>
					</Stack>
				</Stack>

				{/* Area Selection for Area Leaders */}
				{isAreaLeader() && AREAS?.length > 1 && (
					<Box sx={{ mb: 2 }}>
						<FormControl variant="outlined" sx={{ minWidth: 200 }}>
							<InputLabel>Select Area</InputLabel>
							<Select
								value={areaId}
								onChange={(e) => setAreaId(e.target.value)}
								label="Select Area"
							>
								{AREAS.map((area) => (
									<MenuItem key={area._id} value={area._id}>
										{area.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
				)}
			</Box>


			{/* Error Alert */}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			)}

			{/* Filters Section */}
			<Paper sx={{ p: 2, mb: 2 }}>
				<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
					<FilterList color="primary" />
					<Typography variant="h6">Filters</Typography>
				</Stack>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							label="Search"
							placeholder="Search by name, phone, or email..."
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<GridSearchIcon />
									</InputAdornment>
								),
							}}
							onChange={handleSearchChange}
							value={searchValue}
							variant="outlined"
							size="small"
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormControl fullWidth variant="outlined" size="small">
							<InputLabel>Area</InputLabel>
							<Select
								multiple
								value={filters.areaId}
								onChange={(e) => onFilterChange(e.target.name, e.target.value)}
								label="Area"
								name="areaId"
								renderValue={(selected) => (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
										{selected.map((value) => (
											<Chip key={value} label={value === 'ALL' ? 'All Areas' : getAreas().find(a => a._id === value)?.name || value} size="small" />
										))}
									</Box>
								)}
							>
								<MenuItem value={'ALL'}>All Areas</MenuItem>
								{getAreas().map((area, i) => (
									<MenuItem value={area._id} key={i}>
										{area.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormControl fullWidth variant="outlined" size="small">
							<InputLabel>Status</InputLabel>
							<Select
								multiple
								value={filters.status}
								onChange={(e) => onFilterChange(e.target.name, e.target.value)}
								label="Status"
								name="status"
								renderValue={(selected) => (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
										{selected.map((value) => (
											<Chip key={value} label={value === 'ALL' ? 'All Status' : EVENT_BOOKING_STATUS[value]} size="small" />
										))}
									</Box>
								)}
							>
								<MenuItem value={'ALL'}>All Status</MenuItem>
								{Object.keys(EVENT_BOOKING_STATUS)
									.filter((el) => ['9', '12', '11', '3'].includes(String(el)))
									.map((statusKey) => (
										<MenuItem value={statusKey} key={statusKey}>
											{EVENT_BOOKING_STATUS[statusKey]}
										</MenuItem>
									))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormControl fullWidth variant="outlined" size="small">
							<InputLabel>Accommodation</InputLabel>
							<Select
								multiple
								value={filters.accomodation}
								onChange={(e) => onFilterChange(e.target.name, e.target.value)}
								label="Accommodation"
								name="accomodation"
								renderValue={(selected) => (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
										{selected.map((value) => (
											<Chip key={value} label={value === 'ALL' ? 'All Types' : ACCOMODATION_CATEGORY[value]?.name || value} size="small" />
										))}
									</Box>
								)}
							>
								<MenuItem value={'ALL'}>All Accommodation Types</MenuItem>
								{[
									ACCOMODATION_CATEGORY.DORMATORY,
									ACCOMODATION_CATEGORY.ROOM,
									ACCOMODATION_CATEGORY.PREMIUM,
								].map((acc, i) => (
									<MenuItem value={acc.id} key={i}>
										{acc.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<FormControl fullWidth variant="outlined" size="small">
							<InputLabel>Transportation</InputLabel>
							<Select
								multiple
								value={filters.transportation}
								onChange={(e) => onFilterChange(e.target.name, e.target.value)}
								label="Transportation"
								name="transportation"
								renderValue={(selected) => (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
										{selected.map((value) => (
											<Chip key={value} label={value === 'ALL' ? 'Both' : value === true ? 'Yes' : 'No'} size="small" />
										))}
									</Box>
								)}
							>
								<MenuItem value={'ALL'}>Both Yes and No</MenuItem>
								<MenuItem value={true}>Yes</MenuItem>
								<MenuItem value={false}>No</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				</Grid>
			</Paper>

			{/* Data Grid */}
			<Paper sx={{ height: '100%', overflow: 'hidden' }}>
				{isMobileScreen() ? (
					// Mobile Card View
					<Box sx={{ p: 1 }}>
						{cashRequests?.data?.map((s) => (
							<Card key={s._id} sx={{ mb: 2, p: 2 }}>
								<Stack spacing={1}>
									<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
										<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
											{s.userId.initiatedName || s.userId.name}
										</Typography>
										<Chip
											label={EVENT_BOOKING_STATUS[s.status].includes('CASH') ? 'Cash' : 'Online'}
											size="small"
											color={EVENT_BOOKING_STATUS[s.status].includes('CASH') ? 'primary' : 'secondary'}
											variant="outlined"
										/>
									</Box>

									<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
										<Typography variant="body2" color="text.secondary">
											Phone: {s.userId.phone}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Area: {s.connectedAreaId.name}
										</Typography>
									</Box>

									<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
										<Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
											Total: ₹{s.finalAmount?.toLocaleString() || '0'}
										</Typography>
										<Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
											Paid: ₹{s.paidAmount?.toLocaleString() || '0'}
										</Typography>
									</Box>

									<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
										<Chip
											label={EVENT_BOOKING_STATUS[s.status]}
											size="small"
											color={
												EVENT_BOOKING_STATUS[s.status]?.includes('PENDING') ? 'warning' :
													EVENT_BOOKING_STATUS[s.status]?.includes('APPROVED') ? 'success' :
														EVENT_BOOKING_STATUS[s.status]?.includes('CANCELLED') ? 'error' : 'default'
											}
											variant="filled"
										/>

										<Box sx={{ display: 'flex', gap: 0.5 }}>
											{!isAreaLeader() && (
												<Tooltip title="Approve Request">
													<IconButton
														onClick={() => approveRequest(s)}
														aria-label="Approve Request"
														color="success"
														size="small"
														sx={{
															backgroundColor: 'success.light',
															'&:hover': { backgroundColor: 'success.main', color: 'white' }
														}}
													>
														<CheckCircleRounded fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
											{s.accomodationCategory === 'Dormitory' &&
												EVENT_BOOKING_STATUS[s.status].includes('CASH') && (
													<Tooltip title="Upgrade to Room">
														<IconButton
															onClick={() => upgradeToRoom(s)}
															aria-label="Upgrade to Room"
															color="info"
															size="small"
															sx={{
																backgroundColor: 'info.light',
																'&:hover': { backgroundColor: 'info.main', color: 'white' }
															}}
														>
															<MeetingRoomRounded fontSize="small" />
														</IconButton>
													</Tooltip>
												)}
											{EVENT_BOOKING_STATUS[s.status].includes('CASH') && (
												<Tooltip title="Cancel Request">
													<IconButton
														onClick={() => disproveRequest(s)}
														aria-label="Cancel Request"
														color="error"
														size="small"
														sx={{
															backgroundColor: 'error.light',
															'&:hover': { backgroundColor: 'error.main', color: 'white' }
														}}
													>
														<CancelRounded fontSize="small" />
													</IconButton>
												</Tooltip>
											)}
										</Box>
									</Box>
								</Stack>
							</Card>
						))}
					</Box>
				) : (
					// Desktop Table View
					<CustomDataGrid
						rows={
							cashRequests?.data?.map((s) => ({
								...s,
								id: s._id,
								name: s.userId.initiatedName || s.userId.name,
								email: s.userId.email,
								phone: s.userId.phone,
								facilitator:
									s.userId.facilitator?.initiatedName ||
									s.userId.facilitator?.name,
								connectedArea: s.connectedAreaId.name,
								transportationAmount: s.transportationAmount,
								transportation:
									s.transportationAmount > 0 ? 'Yes' : 'No',
								accomodationCategory:
									ACCOMODATION_CATEGORY[s.accomodationCategory]?.name,
								status: EVENT_BOOKING_STATUS[s.status].includes('CASH')
									? 'Cash'
									: 'Online',
								status_detailed: EVENT_BOOKING_STATUS[s.status],
								paidAmount: s.paidAmount,
								subsidy: s.subsidy,
							})) || []
						}
						columns={headCells.map((col) => ({
							...col,
							sortable: true,
							disableClickEventBubbling: true,
							...(isMobileScreen()
								? rowWidthConfig[col.field]?.mobile ||
								rowWidthConfig[col.field]?.default
								: rowWidthConfig[col.field]?.default),
						}))}
						pageSize={filters.pageSize}
						rowCount={cashRequests?.total_results}
						loading={loading}
						initialState={{
							pagination: {
								paginationModel: {
									pageSize: filters.pageSize,
								},
							},
						}}
						paginationModel={filters}
						onPaginationModelChange={(page) =>
							onPageSizeChange({
								pageSize: page.pageSize,
								page: page.pageSize != filters.pageSize ? 0 : page.page,
							})
						}
						sx={{
							'& .MuiDataGrid-root': {
								border: 'none',
							},
							'& .MuiDataGrid-cell': {
								borderBottom: '1px solid #f0f0f0',
							},
							'& .MuiDataGrid-columnHeaders': {
								backgroundColor: '#f5f5f5',
								borderBottom: '2px solid #e0e0e0',
							},
							'& .MuiDataGrid-row:hover': {
								backgroundColor: '#f8f9fa',
							},
						}}
					/>
				)}
			</Paper>
		</Box>
	)
}

export default CashScreen
